import { Package, AlertTriangle } from 'lucide-react';
import type { InventoryStockLevel } from '../../types/spray';
import { useOrgTimezone, formatDateInTz } from '@/utils/timezone';

interface InventoryStockTableProps {
  stock: InventoryStockLevel[];
  onRecordPurchase?: (chemicalId: number, locationId: number) => void;
  onAdjust?: (row: InventoryStockLevel) => void;
}

function InventoryStockTable({ stock = [], onRecordPurchase, onAdjust }: InventoryStockTableProps) {
  const orgTz = useOrgTimezone();

  if (!stock.length) {
    return (
      <div className="text-center py-8 text-neutral-400">
        <Package className="h-10 w-10 mx-auto mb-2 opacity-40" />
        <p className="text-sm">No stock records yet</p>
        <p className="text-xs mt-1">Record a purchase to get started</p>
      </div>
    );
  }

  // Group by location
  const byLocation = stock.reduce<Record<string, InventoryStockLevel[]>>((acc, row) => {
    const key = row.location_name || `Location ${row.location_id}`;
    if (!acc[key]) acc[key] = [];
    acc[key].push(row);
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      {Object.entries(byLocation).map(([locationName, rows]) => (
        <div key={locationName} className="border border-neutral-200 rounded-lg overflow-hidden">
          <div className="bg-neutral-50 px-4 py-2.5 border-b border-neutral-200">
            <div className="flex items-center gap-2">
              <Package className="h-4 w-4 text-neutral-500" />
              <span className="text-sm font-semibold text-neutral-700">{locationName}</span>
            </div>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-neutral-100 bg-neutral-50/50">
                <th className="text-left px-4 py-2 text-xs font-medium text-neutral-500 uppercase tracking-wide">Chemical</th>
                <th className="text-right px-4 py-2 text-xs font-medium text-neutral-500 uppercase tracking-wide">Stock</th>
                <th className="text-right px-4 py-2 text-xs font-medium text-neutral-500 uppercase tracking-wide">Last movement</th>
                {(onRecordPurchase || onAdjust) && <th className="px-4 py-2" />}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => {
                const stockNum = parseFloat(String(row.current_stock));
                // Out of stock is the only judgement this table can honestly
                // make. There used to be an amber `stockNum < 10` alongside it,
                // which meant nothing: the threshold took no account of how
                // much of the product an application actually draws, so 9 L of
                // something applied at 50 mL/ha went amber while 12 L of
                // something applied at 5 L/ha — a couple of sprays from empty —
                // read as fine. A warning that is wrong in both directions gets
                // ignored, including on the row where it was right. Reinstate a
                // low-stock signal only against a per-chemical reorder level or
                // recorded usage, never a bare number.
                const isOut = stockNum <= 0;

                return (
                  <tr key={idx} className={`border-b border-neutral-50 last:border-b-0 ${isOut ? 'bg-red-50' : ''}`}>
                    <td className="px-4 py-3">
                      <div>
                        <span className="font-medium text-neutral-800">{row.product_name}</span>
                        {row.trade_name && (
                          <span className="text-neutral-400 text-xs ml-1">({row.trade_name})</span>
                        )}
                        {row.formulation_type && (
                          <span className="text-xs text-neutral-400 ml-1">[{row.formulation_type}]</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        {isOut && <AlertTriangle className="h-4 w-4 text-red-500" />}
                        <span className={`font-semibold ${isOut ? 'text-red-600' : 'text-neutral-800'}`}>
                          {stockNum.toFixed(2)} {row.unit}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right text-xs text-neutral-400">
                      {row.last_movement_at
                        ? formatDateInTz(row.last_movement_at, orgTz)
                        : '—'
                      }
                    </td>
                    {(onRecordPurchase || onAdjust) && (
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {onAdjust && (
                          <button
                            onClick={() => onAdjust(row)}
                            className="text-xs text-neutral-500 hover:text-neutral-800 font-medium"
                          >
                            Adjust
                          </button>
                        )}
                        {onRecordPurchase && onAdjust && (
                          <span className="text-neutral-300 mx-2">|</span>
                        )}
                        {onRecordPurchase && (
                          <button
                            onClick={() => onRecordPurchase(row.chemical_id, row.location_id)}
                            className="text-xs text-primary-600 hover:text-primary-800 font-medium"
                          >
                            + Add stock
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

export default InventoryStockTable;
